import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { ModelService } from '../../../../services/vehicles/model-service';
import { Model } from '../../../../models/model';
import { Car } from '../../../../models/car';
import { FormControl } from '@angular/forms';
import { CarService } from '../../../../services/vehicles/car-service';
import { PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-model-details',
  imports: [TableComponent],
  templateUrl: './model-details.html',
  styleUrl: './model-details.scss'
})
export class ModelDetails implements OnInit {

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<Car>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    { header: 'Plate number', field: 'plateNo', sortable: true },
    {
      header: 'Name',
      field: 'model.maker',
      bottomField: 'model.name',
      sortable: true
    },
    { header: 'Model', field: 'model.name', sortable: true, splitAfterSpace: true },
    { header: 'Type', field: 'model.bodyType', sortable: true, splitAfterSpace: true },
    {
      field: 'actions',
      header: 'Actions',
      sortable: false,
      actions: [
        {
          icon: 'fa-regular fa-eye',
          command: (car) => this.redirectToCarDetails(car),
          tooltip: 'View Details',
        },
      ],
    },
  ];

  carsList = signal<Car[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;
  searchControl = new FormControl('');

  activatedID: string;
  modelDetails = signal<Model | undefined>(undefined)

  private modelService = inject(ModelService)
  private carService = inject(CarService)
  private activatedRoute = inject(ActivatedRoute)
  private router = inject(Router)

  ngOnInit(): void {
    this.getModelDetails()
    this.onSearchChange()
  }

  getModelDetails = () => {
    this.activatedRoute.paramMap.pipe(
      switchMap((params: ParamMap) => {
        this.activatedID = params.get('id') as string
        return this.modelService.getModelById(this.activatedID)
      })
    ).subscribe({
      next: (res) => {
        this.modelDetails.set(res.data)
        this.getCarsList()
      }
    })
  }

  getCarsList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          modelId: this.activatedID,
          sortBy: queryParams['sortBy'],
          sortDirection: queryParams['sortDirection'],
        };
        return this.carService.getCarsList(params);
      })
    )
      .subscribe((res) => {
        this.carsList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  onSearchChange = () => {
    this.searchControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe((value) => {
      if (value == "") {
        value = ''
      }
      this.router.navigate([], {
        queryParams: {
          search: value,
          page: undefined,
          size: undefined,
        },
        queryParamsHandling: "merge"
      })
    })
  }

  redirectToCarDetails = (car: Car) => {
    this.router.navigate([`vehicles/${car.id}`])
  }
}
